<template>
  <div
    id="activity"
    class="w-full max-w-screen-2xl mx-auto"
  >
    <div class="flex flex-col-reverse lg:flex-row-reverse gap-6 2xl:gap-10 lg:justify-between">
      <!-- Right-hand main activity (visual right, DOM first) -->
      <div
        v-paywall="paywallOptions"
        :class="paywallOptions.showPaywall ? 'max-h-[900px] overflow-y-hidden bg-surface-light border border-surface-dark rounded-md px-4 overflow-hidden relative' : ''"
        class="main-activity flex-grow min-w-0 lg:basis-2/3 2xl:max-w-[1040px] py-6 rounded-md border border-solid border-weak bg-surface-light"
      >
        <h2 class="text-lg px-4 font-bold lg:px-8 ">
          {{ activeFilterTitle }}
        </h2>
        <div
          id="profile-activity-list"
          data-test-id="profile-activity-list"
          class="mt-6 w-full lg:px-4"
        >
          <AM3ActivityList
            :activities="computedActivity"
            :loading="isFetchingActivity"
            :show-more="hasMoreActivity"
            :pending-count="customerState.pendingActivityCount"
            @show-more-click="loadMoreActivity"
          />
        </div>
      </div>

      <!-- Left-hand sidebar (visual left, DOM last) -->
      <div
        class="sidebar w-full lg:basis-1/3 2xl:max-w-[320px] shrink-0 min-w-0"
      >
        <div
          class="sticky top-4 p-4 md:p-0 rounded-md border border-solid border-weak bg-surface-light"
        >
          <h3 class="text-lg font-bold py-6 px-4 md:px-8">
            Filter activity
          </h3>
          <ul class="space-y-2 pb-6 px-4">
            <li
              v-for="filter in activityFilters"
              :key="filter.key"
              class="block"
            >
              <button
                class="w-full hover:bg-stone-50 text-left px-4 py-2 rounded-md text-sm transition-colors duration-200 outline-1 outline outline-transparent focus:outline-brand font-medium"
                :class="{
                  'text-brand bg-surfaces-neutral': activeFilter === filter.key,
                  'text-primary': activeFilter !== filter.key,
                }"
                @click="setActiveFilter(filter.key)"
              >
                {{ filter.title }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { getComputedActivity } from '@/utils/fan-activity'
import AM3ActivityList from '@/components/am3/activity-list/AM3ActivityList.vue'
import { usePausableInterval } from '@/composables/usePausableInterval'

const store = useStore()
const route = useRoute()
const router = useRouter()

// Reactive state
const activeFilter = ref('all')

// Getters
const isFeatureEnabled = computed(() => store.getters['authModule/isFeatureEnabled'])
const isPermissionEnabled = permissions => store.getters['authModule/isPermissionEnabled'](permissions)

// State
const customerState = computed(() => store.state.customerModule)
const customer = computed(() => store.state.customerModule.customer)
const isFetchingActivity = computed(() => store.state.customerModule.isFetchingActivity)

// Activity filter configuration
const activityFilters = computed(() => {
  const filters = [
    {
      key: 'all',
      title: 'All activity',
      typeKeys: [],
      notTypeKeys: [],
    },
    {
      key: 'fan-message',
      title: 'Messaging',
      typeKeys: ['fan-message'],
      notTypeKeys: [],
    },
    {
      key: 'subscription',
      title: 'List subscriptions',
      typeKeys: ['subscription'],
      notTypeKeys: [],
    },
    {
      key: 'campaign-registration',
      title: 'Data capture',
      typeKeys: ['campaign-registration'],
      notTypeKeys: [],
    },
  ]

  // Add sales-specific filters if user has permissions
  if (isPermissionEnabled(['view-sales'])) {
    filters.push(
      {
        key: 'event-purchase',
        title: 'Ticket sales',
        typeKeys: ['event-purchase'],
        notTypeKeys: [],
      },
      {
        key: 'event-attendance',
        title: 'Event attendance',
        typeKeys: ['event-attendance'],
        notTypeKeys: [],
      },
    )
  }

  return filters
})

// Computed properties
const isCustomerProfileFeatureEnabled = computed(() => {
  return isFeatureEnabled.value(['customer-profiles'])
})

const paywallOptions = computed(() => ({
  showPaywall: !isCustomerProfileFeatureEnabled.value,
  title: 'Unlock activity feed',
  description: 'View detailed activity history for your contacts including messaging, campaigns, and events.',
  buttonText: 'Upgrade Plan',
  buttonIcon: 'rocket',
  linkDestination: '/upgrade',
}))

const allowedTypeKeys = computed(() => {
  if (isPermissionEnabled(['view-sales'])) {
    return ['fan-message', 'campaign-registration', 'event-purchase', 'event-attendance', 'ecommerce-purchase', 'subscription']
  }
  return ['fan-message', 'campaign-registration', 'subscription']
})

const computedActivity = computed(() => {
  // Use raw server data when available, otherwise fall back to processed data
  // if (isCustomerProfileFeatureEnabled.value && customerState.value.rawFanActivity?.length > 0) {
  //   // Return raw server objects with minimal processing
  //   const rawData = customerState.value.rawFanActivity.map((activity: any) => ({
  //     ...activity,
  //     dateTime: activity.activityTime, // Add dateTime for AM3Formatter compatibility
  //   }))
  //   return rawData
  // }

  // Fallback for when customer profiles feature is disabled (dummy data)
  return getComputedActivity(
    isCustomerProfileFeatureEnabled.value,
    activeFilter.value,
    customerState.value.rawFanActivity,
  )
})

const hasMoreActivity = computed(() => {
  return !customerState.value.noMoreActivity && !customerState.value.hasFetchCustomerActivityFailed
})

const activeFilterTitle = computed(() => {
  const filter = activityFilters.value.find(f => f.key === activeFilter.value)
  return filter ? filter.title : 'Activity'
})

// Methods
const setActiveFilter = (filterKey: string) => {
  activeFilter.value = filterKey

  // Reset activity data when changing filters
  store.commit('customerModule/RESET_CUSTOMER_ACTIVITY')

  // Fetch new activity based on filter
  nextTick(() => {
    fetchActivity()
  })
}

const fetchActivity = async ({
  top = 50,
  skip = 0,
  append = false,
} = {}) => {
  if (customerState.value.isFetchingActivity) return
  if (!isCustomerProfileFeatureEnabled.value) return

  const selectedFilter = activityFilters.value.find(item => item.key === activeFilter.value)
  if (!selectedFilter) return

  const types = selectedFilter.typeKeys.length > 0 ? selectedFilter.typeKeys : allowedTypeKeys.value

  await store.dispatch('customerModule/FETCH_CUSTOMER_ACTIVITY', {
    top,
    skip,
    types,
    notTypes: selectedFilter.notTypeKeys,
    append,
    fanOid: route.params.customer,
  })
}

const loadMoreActivity = async () => {
  if (customerState.value.noMoreActivity || customerState.value.isFetchingActivity) return

  await fetchActivity({
    skip: customerState.value.fanActivity.length,
    append: true,
  })
}

// Watchers - Only fetch activity when component is visible/active
watch(
  () => customer.value,
  (newCustomer, oldCustomer) => {
    // Fetch activity when customer changes (initial load or navigation)
    if (newCustomer && newCustomer.oid && !isFetchingActivity.value) {
      // Check if this is a different customer or initial load
      if (!oldCustomer || oldCustomer.oid !== newCustomer.oid) {
        if (isCustomerProfileFeatureEnabled.value) {
          fetchActivity()
        }
      }
    }
  },
  { immediate: false }, // Don't run immediately to avoid conflicts with overview
)

// The backend can take a while (background processing) to reflect an edit as new activity, so
// polling faster than that just wastes requests. Poll briskly while activity is expected, then
// back off exponentially while nothing new shows up, settling into a slow idle heartbeat.
const ACTIVE_POLL_INTERVAL_MS = 2_000
const IDLE_POLL_INTERVAL_MS = 15_000
const MAX_POLL_INTERVAL_MS = 30_000

let currentPollIntervalMs = ACTIVE_POLL_INTERVAL_MS

const pollActivity = async () => {
  if (!customer.value || !customer.value.oid || !isCustomerProfileFeatureEnabled.value) return

  await fetchActivity()

  currentPollIntervalMs = customerState.value.pendingActivityCount > 0
    ? Math.min(currentPollIntervalMs * 2, MAX_POLL_INTERVAL_MS)
    : IDLE_POLL_INTERVAL_MS
  restartActivityPoll()
}

// Poll for new activity while this tab is open and the browser tab is visible
const { restart: restartActivityPoll } = usePausableInterval(pollActivity, () => currentPollIntervalMs)

// Lifecycle hooks
onMounted(async () => {
  await store.dispatch('authModule/FETCH_PERMISSIONS')
  store.commit('customerModule/RESET_CUSTOMER_ACTIVITY')

  // Only fetch activity if we have customer data and feature is enabled
  if (customer.value && customer.value.oid && isCustomerProfileFeatureEnabled.value) {
    fetchActivity()
  }
})

onBeforeUnmount(() => {
  // Don't reset activity data when unmounting, let the layout handle cleanup
  // This prevents clearing data when switching between tabs
})
</script>
